import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { organisationApi } from '../../api/endpoints.js';
import type { Department } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ConfirmDialog,
  DataTable,
  Dialog,
  Form,
  FormSection,
  NumberField,
  PageHeader,
  SelectField,
  StatusBadge,
  SwitchField,
  TextAreaField,
  TextField,
  useToast,
  type DataColumn,
  SaveErrorNotice,
} from '../../components/ui/index.js';
import { ApiError } from '../../api/client.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { optionalText } from '../clients/bilingual.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * The laboratory's own departments.
 *
 * A register, not free text, and nothing in it ships with the product: every
 * row belongs to the laboratory that created it (CLAUDE.md section 6.1,
 * decision D53).
 *
 * DELETE ONLY WHAT NOTHING EVER CITED. The server says so per row
 * (`canDelete`) and enforces it again on the request (`department_in_use`).
 * A department that any record names, now or in the past, is deactivated
 * instead: those records keep pointing at it, because that is where the work
 * was done.
 */
export function DepartmentsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();

  const canManage = can(PERMISSIONS.DEPARTMENTS_MANAGE);

  const [editing, setEditing] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState<Department | null>(null);
  const [isCreating, setCreating] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [branchId, setBranchId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<number | null>(null);
  const [isActive, setActive] = useState(true);
  const [reason, setReason] = useState('');

  const departments = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => organisationApi.listDepartments(true),
  });

  const branches = useQuery({
    queryKey: ['branches', 'all'],
    queryFn: () => organisationApi.listBranches(true),
  });

  /** What already names this department. Reported, never used to refuse. */
  const usage = useQuery({
    queryKey: ['department', editing?.id],
    queryFn: () => organisationApi.getDepartment(editing?.id as string),
    enabled: editing !== null,
  });

  const isOpen = isCreating || editing !== null;

  const openCreate = () => {
    setEditing(null);
    setCode('');
    setName('');
    setDescription('');
    setBranchId(null);
    setSortOrder(null);
    setActive(true);
    setReason('');
    save.reset();
    setCreating(true);
  };

  const openEdit = (department: Department) => {
    setCreating(false);
    setCode(department.code ?? '');
    setName(department.name);
    setDescription(department.description ?? '');
    setBranchId(department.branch?.id ?? null);
    setSortOrder(department.sortOrder);
    setActive(department.isActive);
    setReason('');
    save.reset();
    setEditing(department);
  };

  const close = () => {
    setCreating(false);
    setEditing(null);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: optionalText(code),
        name: name.trim(),
        description: optionalText(description),
        // Explicitly null when none is chosen: a single-site laboratory has
        // departments and no meaningful branch to hang them from, and
        // demanding one would mean inventing a branch.
        branchId,
        ...(sortOrder === null ? {} : { sortOrder }),
      };
      if (editing) {
        return organisationApi.updateDepartment(editing.id, {
          ...body,
          isActive,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
      }
      return organisationApi.createDepartment(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['departments'] });
      await queryClient.invalidateQueries({ queryKey: ['catalogue'] });
      close();
      show({ tone: 'success', message: t('organisation.departments.saved') });
    },
  });

  const remove = useMutation({
    mutationFn: (department: Department) =>
      organisationApi.deleteDepartment(department.id),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['departments'] });
      show({ tone: 'success', message: t('organisation.departments.deleted') });
    },
    onError: async (error) => {
      // Someone cited it since the list was loaded: show the real state.
      if (error instanceof ApiError && error.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['departments'] });
      }
    },
  });

  const columns: readonly DataColumn<Department>[] = [
    {
      key: 'name',
      header: t('organisation.departments.name'),
      render: (department) => (
        <div className="min-w-0">
          <span className="block truncate text-default-900">
            {department.name ?? t('common.notRecorded')}
          </span>
          {department.code ? (
            <span className="text-caption text-default-600" dir="ltr">
              {department.code}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'branch',
      header: t('organisation.departments.branch'),
      render: (department) => (
        <span className="text-default-600">
          {department.branch
            ? department.branch.name
            : t('organisation.departments.noBranch')}
        </span>
      ),
    },
    {
      key: 'sortOrder',
      header: t('organisation.departments.sortOrder'),
      align: 'end',
      render: (department) => <span dir="ltr">{department.sortOrder}</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (department) => (
        <StatusBadge
          label={department.isActive ? t('common.active') : t('common.inactive')}
          tone={department.isActive ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (department) =>
        canManage ? (
          <div className="flex justify-end gap-1">
            <ActionButton
              priority="tertiary"
              size="sm"
              onPress={() => openEdit(department)}
            >
              {t('common.edit')}
            </ActionButton>
            {department.canDelete ? (
              <ActionButton
                priority="tertiary"
                size="sm"
                onPress={() => {
                  remove.reset();
                  setDeleting(department);
                }}
              >
                {t('organisation.departments.delete')}
              </ActionButton>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('organisation.departments.title')}
        subtitle={t('organisation.departments.subtitle')}
        breadcrumbs={[
          { label: t('nav.settings'), to: '/settings' },
          { label: t('organisation.departments.title') },
        ]}
        actions={
          canManage ? (
            <ActionButton priority="primary" onPress={openCreate}>
              {t('organisation.departments.create')}
            </ActionButton>
          ) : null
        }
      />

      <RegisterPanel>
        <DataTable
          label={t('organisation.departments.title')}
          columns={columns}
          rows={departments.data?.data ?? []}
          rowKey={(department) => department.id}
          isLoading={departments.isLoading}
          error={departments.error}
          onRetry={() => void departments.refetch()}
          emptyContent={t('organisation.departments.noneYet')}
        />
      </RegisterPanel>

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title={t('organisation.departments.deleteTitle')}
        message={t('organisation.departments.deleteMessage', {
          name: deleting?.name ?? '',
        })}
        confirmLabel={t('organisation.departments.delete')}
        confirmPriority="destructive"
        isPending={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting);
        }}
      >
        {remove.error ? (
          remove.error instanceof ApiError &&
          remove.error.details.some((d) => d.code === 'department_in_use') ? (
            <Alert tone="warning" title={t('organisation.departments.inUse')} />
          ) : (
            <ErrorNotice error={remove.error} />
          )
        ) : null}
      </ConfirmDialog>

      <Dialog
        isOpen={isOpen}
        onClose={close}
        title={
          editing
            ? t('organisation.departments.editTitle')
            : t('organisation.departments.create')
        }
        size="lg"
      >
        {editing && usage.data ? (
          <div className="mb-stack">
            <Alert
              tone="info"
              title={t('organisation.departments.usageTitle')}
              description={t('organisation.departments.usageCounts', {
                catalogueItems: usage.data.usage.catalogueItems,
                requestedServices: usage.data.usage.requestedServices,
              })}
            />
          </div>
        ) : null}

        {editing && !isActive ? (
          <div className="mb-stack">
            <Alert
              tone="warning"
              title={t('organisation.departments.deactivateNotice')}
              description={t('organisation.departments.deactivateHint')}
            />
          </div>
        ) : null}

        <Form
          onSubmit={() => save.mutate()}
          isSubmitting={save.isPending}
          actions={
            <ActionBar>
              <ActionButton priority="tertiary" onPress={close}>
                {t('common.cancel')}
              </ActionButton>
              <ActionButton
                priority="primary"
                type="submit"
                isLoading={save.isPending}
              >
                {t('common.save')}
              </ActionButton>
            </ActionBar>
          }
        >
          <FormSection title={t('organisation.departments.title')} columns={1}>
            <TextField
              label={t('organisation.departments.name')}
              value={name}
              onChange={setName}
              textDirection="auto"
              isRequired
            />
            <TextAreaField
              label={t('organisation.departments.description')}
              value={description}
              onChange={setDescription}
              rows={2}
              textDirection="auto"
            />
          </FormSection>

          <FormSection title={t('common.details')}>
            <TextField
              label={t('organisation.departments.code')}
              value={code}
              onChange={setCode}
            />
            <SelectField
              label={t('organisation.departments.branch')}
              placeholder={t('organisation.departments.noBranch')}
              description={t('organisation.departments.branchHint')}
              value={branchId}
              onChange={setBranchId}
              options={(branches.data?.data ?? []).map((branch) => ({
                value: branch.id,
                label:
                  branch.name ?? branch.code ?? '',
              }))}
            />
            <NumberField
              label={t('organisation.departments.sortOrder')}
              description={t('organisation.departments.sortOrderHint')}
              value={sortOrder}
              onChange={setSortOrder}
              step="1"
            />
            {editing ? (
              <>
                <SwitchField
                  label={t('common.active')}
                  description={
                    editing.canDelete
                      ? t('organisation.departments.activeHint')
                      : t('organisation.departments.inUse')
                  }
                  value={isActive}
                  onChange={setActive}
                />
                <TextAreaField
                  label={t('common.reasonOptional')}
                  value={reason}
                  onChange={setReason}
                  rows={2}
                />
              </>
            ) : null}
          </FormSection>

          {save.error ? <SaveErrorNotice error={save.error} fieldLabels={{ name: t('organisation.departments.name'), code: t('organisation.departments.code'), branchId: t('organisation.departments.branch'), sortOrder: t('organisation.departments.sortOrder') }} /> : null}
        </Form>
      </Dialog>
    </>
  );
}
